import React, { useCallback, useEffect, useRef, useState } from 'react';
import { SUBTLE, MOBILE_TITLE_BAR } from './tokens';
import { useSoft, RING_ON } from './SurfaceStyleContext';

/**
 * Mobile tab strip: horizontally scrolling, sticky directly under the collapsing title bar
 * (`[data-mob-header]`). The title bar's real height is measured at runtime rather than
 * assumed, or the strip parks a couple of px under it.
 *
 * Tab pin: tapping a tab scrolls the window so the strip lands at the top of the fold.
 * The new tab's content can mount a frame late and shorten the scroll range, so the pin is
 * retried a few times. Tab content should carry `min-height: 680px` so short tabs can still
 * scroll into position.
 */
/** The nearest ancestor that actually scrolls, or null when the window does. */
export function scrollParentOf(el) {
  let p = el?.parentElement;
  while (p && p !== document.body && p !== document.documentElement) {
    const { overflowY } = window.getComputedStyle(p);
    if ((overflowY === 'auto' || overflowY === 'scroll') && p.scrollHeight > p.clientHeight) return p;
    p = p.parentElement;
  }
  return null;
}

export default function MobileTabStrip({ tabs = [], active, onChange, pin = true, className = '', headerSelector = '[data-mob-header]', stickyTop, testId = 'mobile-tab-strip' }) {
  const ref = useRef(null);
  const [top, setTop] = useState(stickyTop ?? MOBILE_TITLE_BAR);
  const fixed = stickyTop !== undefined && stickyTop !== null;

  // `stickyTop` pins the strip at a known offset (0 when the title bar sits outside the
  // scroller, as in the event sheet); otherwise the title bar's height is measured.
  const measure = useCallback(() => {
    if (fixed) return stickyTop;
    const header = document.querySelector(headerSelector);
    if (!header) return MOBILE_TITLE_BAR;
    const h = Math.round(header.getBoundingClientRect().height);
    if (h > 0) setTop(h);
    return h || MOBILE_TITLE_BAR;
  }, [headerSelector, fixed, stickyTop]);

  useEffect(() => {
    if (fixed) { setTop(stickyTop); return undefined; }
    measure();
    const header = document.querySelector(headerSelector);
    if (!header || typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(() => measure());
    ro.observe(header);
    return () => ro.disconnect();
  }, [measure, headerSelector, fixed, stickyTop]);

  // Pin scrolls whatever the strip actually scrolls in — the window on a route, the page's
  // own scroller inside a sheet (scrolling the window there would move the page behind).
  const pinTabs = useCallback((tries = 0) => {
    const strip = ref.current;
    if (!strip) return;
    const h = measure();
    const parent = scrollParentOf(strip);
    const current = () => (parent ? parent.scrollTop : window.scrollY);
    const to = parent
      ? Math.max(0, strip.getBoundingClientRect().top - parent.getBoundingClientRect().top + parent.scrollTop - h)
      : Math.max(0, strip.getBoundingClientRect().top + window.scrollY - h);
    if (Math.abs(current() - to) < 2) return;
    if (parent) parent.scrollTo({ top: to }); else window.scrollTo({ top: to });
    if (tries < 5) {
      window.setTimeout(() => {
        if (Math.abs(current() - to) > 4) pinTabs(tries + 1);
      }, 60);
    }
  }, [measure]);

  const pick = (key) => {
    onChange?.(key);
    if (pin) window.setTimeout(() => pinTabs(), 0);
  };

  const soft = useSoft();
  if (soft) {
    // Soft: scrolling 36px pills on the page wash, the active one black with the ring.
    return (
      <div
        ref={ref}
        data-mob-strip="1"
        role="tablist"
        className={`sticky z-[15] flex gap-[6px] overflow-x-auto [&::-webkit-scrollbar]:hidden ${className}`}
        style={{ top, scrollbarWidth: 'none', padding: '8px 12px', background: '#F5F5F5' }}
        data-testid={testId}
        data-soft="1"
      >
        {tabs.map((t) => {
          const on = t.key === active;
          return (
            <button
              key={t.key}
              type="button"
              role="tab"
              aria-selected={on}
              data-tab={t.key}
              onClick={() => pick(t.key)}
              className="flex-none whitespace-nowrap rounded-full font-mono font-bold uppercase transition-colors duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-black focus-visible:ring-inset"
              style={{
                height: 36, padding: '0 14px', fontSize: 10, letterSpacing: '0.14em', border: '2px solid transparent', boxSizing: 'border-box',
                background: on ? RING_ON : '#fff',
                color: on ? '#fff' : t.gated ? '#C9CDD3' : t.danger ? '#DC2626' : '#000',
              }}
            >
              {t.label}{t.count !== undefined && t.count !== null && t.count !== '' ? ` ${t.count}` : ''}
            </button>
          );
        })}
      </div>
    );
  }

  return (
    <div
      ref={ref}
      data-mob-strip="1"
      role="tablist"
      className={`sticky z-[15] flex overflow-x-auto border-b-2 border-black bg-white [&::-webkit-scrollbar]:hidden ${className}`}
      style={{ top, scrollbarWidth: 'none' }}
      data-testid={testId}
    >
      {tabs.map((t) => {
        const on = t.key === active;
        return (
          <button
            key={t.key}
            type="button"
            role="tab"
            aria-selected={on}
            data-tab={t.key}
            onClick={() => pick(t.key)}
            className="flex-none whitespace-nowrap bg-white font-mono font-bold uppercase transition-colors duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-black focus-visible:ring-inset"
            style={{
              padding: '13px 15px',
              fontSize: 11,
              letterSpacing: '0.05em',
              borderBottom: on ? '3px solid #000' : '3px solid transparent',
              color: on ? '#000' : t.gated ? '#C9CDD3' : t.danger ? '#DC2626' : SUBTLE,
              minHeight: 44,
            }}
          >
            {t.label}{t.count !== undefined && t.count !== null && t.count !== '' ? ` ${t.count}` : ''}
          </button>
        );
      })}
    </div>
  );
}
